import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { TrendingUp, DollarSign, Calendar, Shield, ArrowRight, Mail, Loader2, Check, User, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const { brandName } = useBrand();
  const { t } = useLanguage();
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [authMode, setAuthMode] = useState("register"); // "register" or "login"
  const [authStep, setAuthStep] = useState("user_type"); // "user_type" -> "company_details" -> "package" -> "email" -> "otp"
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [packages, setPackages] = useState([]);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  
  // User type selection state
  const [userType, setUserType] = useState("individual"); // "individual" or "company"
  const [companyName, setCompanyName] = useState("");
  const [companyIndustry, setCompanyIndustry] = useState("");
  const [companySize, setCompanySize] = useState("");

  useEffect(() => {
    if (user) {
      if (user.role === "admin") {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
    }
  }, [user, navigate]);

  useEffect(() => {
    // Fetch packages when dialog opens
    if (showAuthDialog && packages.length === 0) {
      fetchPackages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAuthDialog]);

  const fetchPackages = async () => {
    try {
      const response = await api.get("/packages");
      setPackages(response.data);
    } catch (error) {
      toast.error(t("failed_to_load", "errors"));
    }
  };

  const handleSelectUserType = (type) => {
    setUserType(type);
    if (type === "company") {
      setAuthStep("company_details");
    } else {
      setAuthStep("package");
    }
  };

  const handleCompanyDetailsNext = () => {
    if (!companyName.trim()) {
      toast.error("Please enter your company name");
      return;
    }
    if (!companyIndustry) {
      toast.error("Please select your industry type");
      return;
    }
    if (!companySize) {
      toast.error("Please select your company size");
      return;
    }
    setAuthStep("package");
  };

  const handleSelectPackage = (pkg) => {
    setSelectedPackage(pkg);
    setAuthStep("email");
  };

  const openRegisterDialog = () => {
    setAuthMode("register");
    setAuthStep("user_type");
    setUserType("individual");
    setCompanyName("");
    setCompanyIndustry("");
    setCompanySize("");
    setShowAuthDialog(true);
  };

  const openLoginDialog = () => {
    setAuthMode("login");
    setAuthStep("email");
    setSelectedPackage(null);
    setShowAuthDialog(true);
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    try {
      await api.post("/auth/register/start", { 
        email,
        user_type: userType,
        company_name: userType === "company" ? companyName : null,
        company_industry: userType === "company" ? companyIndustry : null,
        company_size: userType === "company" ? companySize : null
      });
      setAuthStep("otp");
      toast.success(t("verification_code_sent", "auth"));
    } catch (error) {
      toast.error(error.response?.data?.detail || t("failed_to_send_code", "auth"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) return;

    setIsLoading(true);
    try {
      const response = await api.post("/auth/register/verify", { 
        email, 
        otp,
        user_type: userType,
        company_name: userType === "company" ? companyName : null,
        company_industry: userType === "company" ? companyIndustry : null,
        company_size: userType === "company" ? companySize : null
      });
      const token = response.data.access_token;
      const userData = response.data.user;
      
      // Set token first to enable API calls
      localStorage.setItem("token", token);
      
      // If user selected a package, assign it now
      if (selectedPackage && !userData.package_id) {
        try {
          const pkgResponse = await api.post("/packages/select", { package_id: selectedPackage.id });
          // Update user data with package info
          userData.package_id = pkgResponse.data.package_id;
          userData.package_name = pkgResponse.data.package_name;
          userData.monthly_limit = pkgResponse.data.monthly_limit;
        } catch (pkgError) {
          console.error("Failed to select package:", pkgError);
        }
      }
      
      login(token, userData);
      toast.success(t("welcome_to", "auth") + ` ${brandName}!`);
      setShowAuthDialog(false);
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || t("invalid_code", "auth"));
    } finally {
      setIsLoading(false);
    }
  };

  const resetAuth = () => {
    setAuthStep(authMode === "register" ? "user_type" : "email");
    setSelectedPackage(null);
    setEmail("");
    setOtp("");
    setUserType("individual");
    setCompanyName("");
    setCompanyIndustry("");
    setCompanySize("");
  };

  const formatLimit = (limit) => {
    if (limit === 0) return t("unlimited", "common");
    return `${limit} ${t("per_month", "auth")}`;
  };

  const features = [
    {
      icon: DollarSign,
      titleKey: "feature_1_title",
      descKey: "feature_1_desc"
    },
    {
      icon: Calendar,
      titleKey: "feature_2_title",
      descKey: "feature_2_desc"
    },
    {
      icon: TrendingUp,
      titleKey: "feature_3_title",
      descKey: "feature_3_desc"
    },
    {
      icon: Shield,
      titleKey: "feature_4_title",
      descKey: "feature_4_desc"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(1200px_600px_at_10%_-10%,hsl(187_60%_96%)_0%,transparent_60%),radial-gradient(1000px_500px_at_110%_-10%,hsl(214_25%_95%)_0%,transparent_55%)]" />
        
        <nav className="relative z-10 max-w-[1200px] mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="size-8 rounded-lg bg-[hsl(var(--ff-primary))] flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-semibold">{brandName}</span>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate("/admin/login")} data-testid="admin-login-nav-button">
              {t("admin", "landing")}
            </Button>
            <Button variant="ghost" onClick={openLoginDialog} data-testid="login-button">
              {t("login", "auth")}
            </Button>
            <Button onClick={openRegisterDialog} data-testid="get-started-button">
              {t("get_started", "landing")} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </nav>

        <div className="relative z-10 max-w-[1200px] mx-auto px-4 py-16 md:py-24">
          <div className="max-w-2xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-foreground">
              {t("hero_title", "landing")} <span className="text-[hsl(var(--ff-primary))]">{t("hero_title_highlight", "landing")}</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground">
              {t("hero_description", "landing")}{" "}
              {brandName} {t("helps_understand", "landing")}
            </p>
            <div className="mt-8 flex gap-4">
              <Button size="lg" onClick={openRegisterDialog} data-testid="hero-cta-button">
                {t("start_free", "landing")} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button size="lg" variant="outline" onClick={openLoginDialog} data-testid="hero-login-button">
                {t("login", "auth")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <section className="max-w-[1200px] mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-semibold">{t("everything_you_need", "landing")}</h2>
          <p className="mt-3 text-muted-foreground">{t("simple_tools", "landing")}</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="features-grid">
          {features.map((feature, index) => (
            <Card key={index} className="border border-border hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="size-10 rounded-lg bg-[hsl(var(--ff-primary)/.1)] flex items-center justify-center mb-3">
                  <feature.icon className="h-5 w-5 text-[hsl(var(--ff-primary))]" />
                </div>
                <CardTitle className="text-lg">{t(feature.titleKey, "landing")}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>{t(feature.descKey, "landing")}</CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[hsl(var(--ff-primary)/.05)] py-16">
        <div className="max-w-[1200px] mx-auto px-4 text-center">
          <h2 className="text-3xl font-semibold">{t("ready_to_start", "landing")}</h2>
          <p className="mt-3 text-muted-foreground">{t("join_thousands", "landing")} {brandName}</p>
          <Button size="lg" className="mt-6" onClick={openRegisterDialog} data-testid="cta-signup-button">
            {t("create_free_account", "landing")}
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-8">
        <div className="max-w-[1200px] mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-[hsl(var(--ff-primary))]" />
            <span className="font-semibold">{brandName}</span>
          </div>
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} {brandName}. {t("all_rights_reserved", "common")}.
          </p>
        </div>
      </footer>

      {/* Auth Dialog with Package Selection */}
      <Dialog open={showAuthDialog} onOpenChange={(open) => { setShowAuthDialog(open); if (!open) resetAuth(); }}>
        <DialogContent className="sm:max-w-lg" data-testid="auth-dialog">
          <DialogHeader>
            <DialogTitle>
              {authStep === "user_type" ? (t("choose_account_type", "auth") || "Choose Account Type") : null}
              {authStep === "company_details" ? (t("company_details", "auth") || "Company Details") : null}
              {authStep === "package" ? (t("choose_your_plan", "auth") || "Choose Your Plan") : null}
              {authStep === "email" ? (authMode === "login" ? (t("welcome_back", "auth") || "Welcome Back") : `${t("sign_up_to", "auth") || "Sign Up to"} ${brandName}`) : null}
              {authStep === "otp" ? (t("verify_your_email", "auth") || "Verify Your Email") : null}
            </DialogTitle>
            <DialogDescription>
              {authStep === "user_type" ? (t("select_account_type_desc", "auth") || "Select the type of account that best fits your needs") : null}
              {authStep === "company_details" ? (t("enter_company_details_desc", "auth") || "Tell us about your business") : null}
              {authStep === "package" ? (t("select_plan_desc", "auth") || "Select a plan to get started") : null}
              {authStep === "email" && authMode === "login" ? (t("enter_email_signin", "auth") || "Enter your email to sign in") : null}
              {authStep === "email" && authMode === "register" ? `${t("you_selected", "auth") || "You selected"}: ${selectedPackage?.name}. ${t("enter_email_continue", "auth") || "Enter your email to continue."}` : null}
              {authStep === "otp" ? `${t("sent_code_to", "auth") || "We sent a 6-digit code to"} ${email}` : null}
            </DialogDescription>
          </DialogHeader>
          
          {/* User Type Selection Step */}
          {authStep === "user_type" && (
            <div className="space-y-3 py-2" data-testid="user-type-selection-step">
              <Card 
                className={`cursor-pointer transition-all hover:border-[hsl(var(--ff-primary))] hover:shadow-md ${userType === "individual" ? "border-[hsl(var(--ff-primary))] bg-[hsl(var(--ff-primary)/.05)]" : ""}`}
                onClick={() => handleSelectUserType("individual")}
                data-testid="user-type-individual"
              >
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="p-3 rounded-full bg-[hsl(var(--ff-primary)/.1)]">
                    <User className="h-6 w-6 text-[hsl(var(--ff-primary))]" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold">{t("personal_finance", "auth") || "Personal Finance"}</p>
                    <p className="text-sm text-muted-foreground">{t("personal_finance_desc", "auth") || "Track personal income, expenses, and savings goals"}</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-muted-foreground" />
                </CardContent>
              </Card>
              
              <Card 
                className={`cursor-pointer transition-all hover:border-[hsl(var(--ff-primary))] hover:shadow-md ${userType === "company" ? "border-[hsl(var(--ff-primary))] bg-[hsl(var(--ff-primary)/.05)]" : ""}`}
                onClick={() => handleSelectUserType("company")}
                data-testid="user-type-company"
              >
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="p-3 rounded-full bg-[hsl(var(--ff-primary)/.1)]">
                    <Building2 className="h-6 w-6 text-[hsl(var(--ff-primary))]" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold">{t("business_finance", "auth") || "Business Finance"}</p>
                    <p className="text-sm text-muted-foreground">{t("business_finance_desc", "auth") || "Manage clients, products, sales, and financial objectives"}</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-muted-foreground" />
                </CardContent>
              </Card>
              
              {authMode === "register" && (
                <div className="text-center pt-2">
                  <button type="button" className="text-sm text-muted-foreground hover:text-foreground underline" onClick={() => { setAuthMode("login"); setAuthStep("email"); setSelectedPackage(null); }}>{t("already_have_account", "auth")}</button>
                </div>
              )}
            </div>
          )}
          
          {/* Company Details Step */}
          {authStep === "company_details" && (
            <div className="space-y-4 py-2" data-testid="company-details-step">
              <div className="space-y-2">
                <Label htmlFor="companyName">{t("company_name", "auth") || "Company Name"}</Label>
                <Input
                  id="companyName"
                  placeholder={t("company_name_placeholder", "auth") || "Enter your company name"}
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  data-testid="company-name-input"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="companyIndustry">{t("industry_type", "auth") || "Industry Type"}</Label>
                <Select value={companyIndustry} onValueChange={setCompanyIndustry}>
                  <SelectTrigger data-testid="company-industry-select">
                    <SelectValue placeholder={t("select_industry", "auth") || "Select your industry type"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="product">{t("product_based", "auth") || "Product-Based"}</SelectItem>
                    <SelectItem value="service">{t("service_based", "auth") || "Service-Based"}</SelectItem>
                    <SelectItem value="both">{t("product_and_service", "auth") || "Both Products & Services"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="companySize">{t("company_size", "auth") || "Company Size"}</Label>
                <Select value={companySize} onValueChange={setCompanySize}>
                  <SelectTrigger data-testid="company-size-select">
                    <SelectValue placeholder={t("select_size", "auth") || "Select your company size"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="solo">{t("solo", "auth") || "Solo (Just me)"}</SelectItem>
                    <SelectItem value="small">{t("small_team", "auth") || "Small (2-10 employees)"}</SelectItem>
                    <SelectItem value="medium">{t("medium_team", "auth") || "Medium (11-50 employees)"}</SelectItem>
                    <SelectItem value="large">{t("large_team", "auth") || "Large (50+ employees)"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setAuthStep("user_type")} className="flex-1">
                  {t("back", "common") || "Back"}
                </Button>
                <Button type="button" onClick={handleCompanyDetailsNext} className="flex-1" data-testid="company-details-next">
                  {t("continue", "common") || "Continue"}
                </Button>
              </div>
            </div>
          )}

          {authStep === "package" && (
            <div className="space-y-3 py-2" data-testid="package-selection-step">
              {/* Show user type badge */}
              <div className="flex items-center gap-2 p-2 bg-muted rounded-lg mb-3">
                {userType === "company" ? (
                  <>
                    <Building2 className="h-4 w-4 text-[hsl(var(--ff-primary))]" />
                    <span className="text-sm font-medium">{companyName}</span>
                    <Badge variant="outline" className="ml-auto">{t("business_account", "auth") || "Business"}</Badge>
                  </>
                ) : (
                  <>
                    <User className="h-4 w-4 text-[hsl(var(--ff-primary))]" />
                    <span className="text-sm font-medium">{t("personal_account", "auth") || "Personal Account"}</span>
                  </>
                )}
              </div>
              
              {packages.length === 0 ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                packages.map((pkg) => (
                  <Card 
                    key={pkg.id}
                    className="cursor-pointer transition-all hover:border-[hsl(var(--ff-primary))] hover:shadow-md"
                    onClick={() => handleSelectPackage(pkg)}
                    data-testid={`package-option-${pkg.id}`}
                  >
                    <CardContent className="p-4 flex items-center justify-between">
                      <div>
                        <p className="font-semibold">{pkg.name}</p>
                        <p className="text-sm text-muted-foreground">{pkg.description}</p>
                      </div>
                      <Badge variant="outline" className="ml-4">
                        {formatLimit(pkg.monthly_limit)}
                      </Badge>
                    </CardContent>
                  </Card>
                ))
              )}
              
              <Button type="button" variant="ghost" onClick={() => setAuthStep(userType === "company" ? "company_details" : "user_type")} className="w-full">
                {t("back", "common") || "Back"}
              </Button>
            </div>
          )}

          {authStep === "email" && (
            <form onSubmit={handleSendOTP} className="space-y-4">
              {authMode === "register" && selectedPackage && (
                <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                  <Check className="h-4 w-4 text-[hsl(var(--ff-primary))]" />
                  <span className="text-sm font-medium">{selectedPackage?.name}</span>
                  <Badge variant="outline" className="ml-auto">
                    {formatLimit(selectedPackage?.monthly_limit)}
                  </Badge>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">{t("email_address", "auth")}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder={t("email_placeholder", "auth")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    data-testid="email-input"
                    required
                  />
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={isLoading} data-testid="send-otp-button">
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {t("send_verification_code", "auth")}
              </Button>
              {authMode === "register" && (
                <Button 
                  type="button"
                  variant="ghost" 
                  className="w-full" 
                  onClick={() => setAuthStep("package")}
                >
                  {t("change_plan", "auth")}
                </Button>
              )}
              <div className="text-center text-sm text-muted-foreground">
                {authMode === "login" ? (
                  <>{t("no_account", "auth")} <button type="button" className="text-[hsl(var(--ff-primary))] hover:underline" onClick={() => { setAuthMode("register"); setAuthStep("package"); }}>{t("sign_up", "auth")}</button></>
                ) : (
                  <>{t("have_account", "auth")} <button type="button" className="text-[hsl(var(--ff-primary))] hover:underline" onClick={() => { setAuthMode("login"); setAuthStep("email"); setSelectedPackage(null); }}>{t("login", "auth")}</button></>
                )}
              </div>
            </form>
          )}

          {authStep === "otp" && (
            <div className="space-y-4">
              <div className="flex justify-center">
                <InputOTP value={otp} onChange={setOtp} maxLength={6} data-testid="otp-input">
                  <InputOTPGroup>
                    <InputOTPSlot index={0} data-testid="otp-cell-0" />
                    <InputOTPSlot index={1} data-testid="otp-cell-1" />
                    <InputOTPSlot index={2} data-testid="otp-cell-2" />
                    <InputOTPSlot index={3} data-testid="otp-cell-3" />
                    <InputOTPSlot index={4} data-testid="otp-cell-4" />
                    <InputOTPSlot index={5} data-testid="otp-cell-5" />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <Button 
                className="w-full" 
                onClick={handleVerifyOTP} 
                disabled={otp.length !== 6 || isLoading}
                data-testid="verify-otp-button"
              >
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {t("verify_and_signin", "auth")}
              </Button>
              <Button 
                variant="ghost" 
                className="w-full" 
                onClick={() => { setAuthStep("email"); setOtp(""); }}
                data-testid="otp-resend-link"
              >
                {t("use_different_email", "auth")}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
